import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  Typography,
  Chip,
  Divider,
} from "@mui/material";
import { useDeleteRouteMutation, useGetRouteQuery } from "../../../Api/attoDeskApi";
import { IRoutes } from "../../../Api/Interface/api.interface";
import NewPopUpTax from "../Edit/index";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import { CiSearch } from "react-icons/ci";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import RouteIcon from "@mui/icons-material/AltRoute";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Pagination from "../../../Components/Pagination";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;
const DAY_SHORT: Record<string, string> = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

/** Convert "HH:MM" 24-hr to "h:MM AM/PM" */
function fmt12(time: string): string {
  if (!time) return "";
  const [hStr, mStr] = time.split(":");
  let h = parseInt(hStr, 10);
  const m = mStr || "00";
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${period}`;
}

/** Parse "HH:MM-HH:MM" range into display string */
function fmtRange(range: string): string {
  if (!range) return "";
  const [start, end] = range.split("-");
  if (!start) return "";
  if (!end) return fmt12(start);
  return `${fmt12(start)} – ${fmt12(end)}`;
}

interface RouteCardProps {
  row: IRoutes;
  index: number;
  onEdit: (row: IRoutes) => void;
  onDelete: (row: IRoutes) => void;
}

const RouteCard: React.FC<RouteCardProps> = ({ row, index, onEdit, onDelete }) => {
  const activeDays = DAYS.filter(
    (d) => row.delivery_schedule?.[d] && row.delivery_schedule[d] !== ""
  );

  const isWeekend = (day: string) => day === "Saturday" || day === "Sunday";

  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #e8eaf0",
        borderRadius: "12px",
        p: 2.5,
        mb: 2,
        transition: "box-shadow 0.2s, border-color 0.2s",
        "&:hover": {
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          borderColor: "#c5cae9",
        },
      }}
    >
      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <RouteIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize={15} color="#1a1a2e" lineHeight={1.2}>
              {row.name}
            </Typography>
            <Typography fontSize={12} color="text.secondary" mt={0.25}>
              {activeDays.length > 0
                ? `${activeDays.length} delivery day${activeDays.length > 1 ? "s" : ""}`
                : "No schedule set"}
            </Typography>
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => onEdit(row)}
            sx={{
              minWidth: 32,
              width: 32,
              height: 32,
              p: 0,
              borderRadius: "8px",
              background: "linear-gradient(135deg, #43a047 0%, #66bb6a 100%)",
              boxShadow: "none",
              "&:hover": { background: "linear-gradient(135deg, #388e3c 0%, #43a047 100%)", boxShadow: "none" },
            }}
          >
            <EditSharpIcon sx={{ fontSize: 15 }} />
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => onDelete(row)}
            sx={{
              minWidth: 32,
              width: 32,
              height: 32,
              p: 0,
              borderRadius: "8px",
              background: "linear-gradient(135deg, #e53935 0%, #ef5350 100%)",
              boxShadow: "none",
              "&:hover": { background: "linear-gradient(135deg, #c62828 0%, #e53935 100%)", boxShadow: "none" },
            }}
          >
            <DeleteOutlineSharpIcon sx={{ fontSize: 15 }} />
          </Button>
        </Box>
      </Box>

      {/* Cities */}
      {Array.isArray(row.cities) && row.cities.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
            <LocationOnIcon sx={{ fontSize: 14, color: "#7986cb" }} />
            <Typography fontSize={11} fontWeight={600} color="#7986cb" textTransform="uppercase" letterSpacing={0.5}>
              Coverage Area
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {row.cities.map((city) => (
              <Chip
                key={city}
                label={city}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 500,
                  bgcolor: "#e8eaf6",
                  color: "#3949ab",
                  border: "none",
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Delivery schedule */}
      {activeDays.length > 0 && (
        <>
          <Divider sx={{ mb: 1.25, borderColor: "#f0f0f5" }} />
          <Box>
            <Typography fontSize={11} fontWeight={600} color="#9e9e9e" textTransform="uppercase" letterSpacing={0.5} mb={0.75}>
              Delivery Schedule
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {activeDays.map((day) => {
                const range = row.delivery_schedule[day] || "";
                return (
                  <Box
                    key={day}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      bgcolor: isWeekend(day) ? "#fff3e0" : "#e3f2fd",
                      border: `1px solid ${isWeekend(day) ? "#ffcc80" : "#90caf9"}`,
                      borderRadius: "8px",
                      px: 1.25,
                      py: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 18,
                        borderRadius: "4px",
                        bgcolor: isWeekend(day) ? "#ff9800" : "#1976d2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography fontSize={9} fontWeight={700} color="#fff" lineHeight={1}>
                        {DAY_SHORT[day]}
                      </Typography>
                    </Box>
                    {range && (
                      <Typography fontSize={11} fontWeight={500} color={isWeekend(day) ? "#e65100" : "#0d47a1"}>
                        {fmtRange(range)}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [selectedCoursing, setSelectedCoursing] = useState<IRoutes | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<IRoutes | null>(null);

  const { data, isLoading, isError, refetch } = useGetRouteQuery({
    searchText: currentSearchQuery,
    id: null,
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleOpenDialog = (coursing: IRoutes) => {
    navigate("/route/create", { state: { routeData: coursing } });
  };

  const handleCloseDialog = () => {
    setSelectedCoursing(null);
    setOpenDialog(false);
  };

  const handleOpenDeletePopup = (coursing: IRoutes) => {
    setCoursingToDelete(coursing);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  useEffect(() => {
    if (debouncedSearchQuery.length >= 3 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const [deleteRoute] = useDeleteRouteMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteRoute(id).unwrap();
      if (response.status) {
        showMessage("Deleted successfully");
        setOpenDeleteCategory(false);
      } else {
        showErrorMessage("Failed to delete the route");
      }
    } catch (error) {
      showErrorMessage("Failed to delete the route");
    }
  };

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
        <CircularProgress />
      </Box>
    );

  const routes: IRoutes[] = Array.isArray(data?.data) ? (data?.data as IRoutes[]) : [];

  const paginated = routes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Card className={classes.card}>
      {/* Header */}
      <Grid
        container
        justifyContent="space-between"
        spacing={5}
        sx={{ borderBottom: 2, padding: 2 }}
        className={classes.titleGrid}
      >
        <Grid item lg={4} md={4} sm={4} xs={4} pt={2} alignContent="center">
          <Typography className={classes.cardTitle}>Delivery Routes</Typography>
        </Grid>
        <Grid
          item
          lg={8}
          md={8}
          sm={8}
          xs={8}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Grid container spacing={1} justifyContent="flex-end" sx={{ display: "flex" }}>
            <Grid item>
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CiSearch className={classes.searchWidgetIcon} />
                    </InputAdornment>
                  ),
                  classes: { root: classes.formControlList },
                }}
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search routes..."
                sx={{ width: "100%" }}
                size="small"
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                className={classes.addButton}
                onClick={() => navigate("/route/create")}
              >
                Add Route
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Summary bar */}
      <Box
        sx={{
          px: 2.5,
          py: 1.25,
          bgcolor: "#f8f9ff",
          borderBottom: "1px solid #e8eaf0",
          display: "flex",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <RouteIcon sx={{ fontSize: 16, color: "#5c6bc0" }} />
          <Typography fontSize={12} color="text.secondary">
            <strong style={{ color: "#3949ab" }}>{routes.length}</strong> route{routes.length !== 1 ? "s" : ""} total
          </Typography>
        </Box>
        {routes.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <LocationOnIcon sx={{ fontSize: 16, color: "#7986cb" }} />
            <Typography fontSize={12} color="text.secondary">
              <strong style={{ color: "#3949ab" }}>
                {Array.from(new Set(([] as string[]).concat(...routes.map((r) => r.cities || [])))).length}
              </strong>{" "}
              cities covered
            </Typography>
          </Box>
        )}
      </Box>

      {/* Cards grid */}
      <Box sx={{ p: 2.5 }}>
        {isError ? (
          <Box textAlign="center" py={6}>
            <Typography color="error">Error fetching routes</Typography>
          </Box>
        ) : routes.length === 0 ? (
          <Box
            textAlign="center"
            py={8}
            sx={{
              background: "#f8f9ff",
              borderRadius: "12px",
              border: "2px dashed #c5cae9",
            }}
          >
            <RouteIcon sx={{ fontSize: 48, color: "#c5cae9", mb: 1 }} />
            <Typography fontSize={15} color="text.secondary" fontWeight={500}>
              No routes yet
            </Typography>
            <Typography fontSize={13} color="text.disabled" mb={2}>
              Create a delivery route to get started
            </Typography>
            <Button
              variant="contained"
              className={classes.addButton}
              onClick={() => navigate("/route/create")}
            >
              Add Route
            </Button>
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              {paginated.map((row, index) => (
                <Grid item xs={12} md={6} lg={4} key={row.id}>
                  <RouteCard
                    row={row}
                    index={page * rowsPerPage + index}
                    onEdit={handleOpenDialog}
                    onDelete={handleOpenDeletePopup}
                  />
                </Grid>
              ))}
            </Grid>

            <Grid
              spacing={5}
              p="18px"
              py={4}
              justifyContent="end"
              container
              textAlign="center"
              alignContent="center"
              alignItems="center"
            >
              <Grid sx={{ textAlign: "end", justifyContent: "flex-end", alignItems: "flex-end" }}>
                <Pagination
                  count={routes.length}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Box>

      {selectedCoursing && (
        <NewPopUpTax
          openModel={openDialog}
          handleCloseDialog={handleCloseDialog}
          data={selectedCoursing}
        />
      )}
      {coursingToDelete && (
        <DeletePopup
          open={openDeleteCategory}
          handleCloseDelete={handleCloseDeletePopup}
          onConfirm={async () => {
            await handleDelete(coursingToDelete.id.toString());
          }}
          title="Delete Route"
          content={`Are you sure you want to delete "${coursingToDelete.name}"?`}
        />
      )}
    </Card>
  );
};

export default Index;
